import React, { useEffect, useState } from 'react';
import { AlertCircle, ArrowLeft, BadgeCheck, Building2, Check, Loader2, MapPin, UserRound, X } from 'lucide-react';
import CookieConsent from '../../components/CookieConsent';
import LegalDocument from '../../components/LegalDocument';
import { BACKOFFICE_API_URL } from '../../config/api';
import { legalDocuments } from '../../content/legalContent';

const emptyAddress = {
  street: '',
  number: '',
  complement: '',
  neighborhood: '',
  city: '',
  state: '',
  zipCode: ''
};

const initialForm = {
  legalName: '',
  tradeName: '',
  documentType: 'CNPJ',
  documentNumber: '',
  companyContactEmail: '',
  companyContactPhone: '',
  ownerName: '',
  ownerEmail: '',
  ownerPhone: '',
  address: emptyAddress,
  planExternalId: '',
  paymentMethod: 'PIX',
  couponCode: '',
  termsAccepted: false,
  privacyAccepted: false,
  billingAuthorized: false
};

const steps = [
  { id: 'business', label: 'Negócio', icon: Building2 },
  { id: 'owner', label: 'Responsável', icon: UserRound },
  { id: 'plan', label: 'Plano', icon: Check },
  { id: 'status', label: 'Status', icon: BadgeCheck }
];

const BR_STATES = [
  { value: 'AC', label: 'AC' }, { value: 'AL', label: 'AL' }, { value: 'AP', label: 'AP' },
  { value: 'AM', label: 'AM' }, { value: 'BA', label: 'BA' }, { value: 'CE', label: 'CE' },
  { value: 'DF', label: 'DF' }, { value: 'ES', label: 'ES' }, { value: 'GO', label: 'GO' },
  { value: 'MA', label: 'MA' }, { value: 'MT', label: 'MT' }, { value: 'MS', label: 'MS' },
  { value: 'MG', label: 'MG' }, { value: 'PA', label: 'PA' }, { value: 'PB', label: 'PB' },
  { value: 'PR', label: 'PR' }, { value: 'PE', label: 'PE' }, { value: 'PI', label: 'PI' },
  { value: 'RJ', label: 'RJ' }, { value: 'RN', label: 'RN' }, { value: 'RS', label: 'RS' },
  { value: 'RO', label: 'RO' }, { value: 'RR', label: 'RR' }, { value: 'SC', label: 'SC' },
  { value: 'SP', label: 'SP' }, { value: 'SE', label: 'SE' }, { value: 'TO', label: 'TO' }
];

const formatMoney = (value) =>
  Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const onlyDigits = (value) => String(value || '').replace(/\D/g, '');

const DRAFT_STORAGE_KEY = '@LeoDelivery:merchantSignupDraftId';

const maskDocument = (value, type) => {
  const digits = onlyDigits(value);
  if (type === 'CPF') {
    return digits
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
      .slice(0, 14);
  }
  return digits
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2')
    .slice(0, 18);
};

const maskCEP = (value) => {
  return onlyDigits(value)
    .replace(/(\d{5})(\d)/, '$1-$2')
    .slice(0, 9);
};

const maskPhone = (value) => {
  const digits = onlyDigits(value);
  if (digits.length <= 10) {
    return digits
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{4})(\d{1,4})$/, '$1-$2')
      .slice(0, 14);
  }
  return digits
    .replace(/(\d{2})(\d{1})(\d{4})(\d{4})/, '($1) $2 $3-$4')
    .slice(0, 16);
};

const SignupPage = () => {
  const params = new URLSearchParams(window.location.search);
  const selectedPlanId = params.get('planId') || '';
  const requestedDraftId = params.get('draftId') || localStorage.getItem(DRAFT_STORAGE_KEY) || '';
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ ...initialForm, planExternalId: selectedPlanId });
  const [draftId, setDraftId] = useState(requestedDraftId);
  const [savingDraft, setSavingDraft] = useState(false);
  const [couponStatus, setCouponStatus] = useState(null);
  const [addressLoading, setAddressLoading] = useState(false);
  const [isAddressFetched, setIsAddressFetched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [signupStatus, setSignupStatus] = useState(null);
  const [error, setError] = useState('');
  const [legalModal, setLegalModal] = useState(null);

  useEffect(() => {
    let mounted = true;
    fetch(`${BACKOFFICE_API_URL}/v1/public/plans`)
      .then((response) => {
        if (!response.ok) throw new Error('plans_unavailable');
        return response.json();
      })
      .then((items) => {
        if (!mounted || !Array.isArray(items)) return;
        setPlans(items);
        setForm((current) => current.planExternalId || !items[0]?.id
          ? current
          : { ...current, planExternalId: items[0].id });
      })
      .catch(() => setError('Nao foi possivel carregar os planos. Tente novamente em alguns instantes.'))
      .finally(() => mounted && setPlansLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!requestedDraftId) return;
    let mounted = true;
    fetch(`${BACKOFFICE_API_URL}/v1/public/merchant-signup-drafts/${requestedDraftId}`)
      .then((response) => {
        if (!response.ok) throw new Error('draft_unavailable');
        return response.json();
      })
      .then((data) => {
        if (!mounted || !data?.draft) return;
        const draft = data.draft;
        setDraftId(draft.id);
        localStorage.setItem(DRAFT_STORAGE_KEY, draft.id);
        setForm((current) => ({
          ...current,
          legalName: draft.legalName || '',
          tradeName: draft.tradeName || '',
          documentType: draft.documentType || 'CNPJ',
          documentNumber: draft.documentNumber ? maskDocument(draft.documentNumber, draft.documentType || 'CNPJ') : '',
          companyContactEmail: draft.companyContactEmail || '',
          companyContactPhone: draft.companyContactPhone ? maskPhone(draft.companyContactPhone) : '',
          ownerName: draft.ownerName || '',
          ownerEmail: draft.ownerEmail || '',
          ownerPhone: draft.ownerPhone || '',
          address: draft.address ? {
            ...emptyAddress,
            ...draft.address,
            zipCode: maskCEP(draft.address.zipCode || '')
          } : emptyAddress,
          planExternalId: draft.planExternalId || selectedPlanId || current.planExternalId,
          paymentMethod: 'PIX',
          couponCode: draft.couponCode || '',
          termsAccepted: Boolean(draft.termsAccepted),
          privacyAccepted: Boolean(draft.privacyAccepted),
          billingAuthorized: Boolean(draft.billingAuthorized)
        }));
        const stepIndex = steps.findIndex((item) => item.id.toUpperCase() === draft.currentStep);
        if (stepIndex >= 0 && stepIndex < 3) setStep(stepIndex);
      })
      .catch(() => setError('Nao foi possivel recuperar o cadastro em andamento.'))
    return () => {
      mounted = false;
    };
  }, [requestedDraftId, selectedPlanId]);

  const update = (field, value) => {
    let finalValue = value;
    if (field === 'documentNumber') {
      finalValue = maskDocument(value, form.documentType);
    }
    if (field === 'companyContactPhone' || field === 'ownerPhone') {
      finalValue = maskPhone(value);
    }
    setForm((current) => ({ ...current, [field]: finalValue }));
    setError('');
  };

  const updateAddress = (field, value) => {
    let finalValue = value;
    if (field === 'zipCode') {
      finalValue = maskCEP(value);
      setIsAddressFetched(false);
      if (onlyDigits(finalValue).length < 8) {
        setForm((current) => ({
          ...current,
          address: {
            ...current.address,
            zipCode: finalValue,
            street: '',
            neighborhood: '',
            city: '',
            state: ''
          }
        }));
        return;
      }
    }
    setForm((current) => ({
      ...current,
      address: { ...current.address, [field]: finalValue }
    }));
    setError('');
  };

  const fetchAddress = async (cep) => {
    const digits = onlyDigits(cep);
    if (digits.length !== 8) return;

    setAddressLoading(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await response.json();
      if (data.erro) {
        setError('CEP nao encontrado.');
        setIsAddressFetched(false);
        return;
      }
      setForm((current) => ({
        ...current,
        address: {
          ...current.address,
          street: data.logradouro || '',
          neighborhood: data.bairro || '',
          city: data.localidade || '',
          state: data.uf || '',
        }
      }));
      setIsAddressFetched(true);
    } catch (err) {
      setError('Erro ao buscar CEP.');
      setIsAddressFetched(false);
    } finally {
      setAddressLoading(false);
    }
  };

  useEffect(() => {
    const digits = onlyDigits(form.address.zipCode);
    if (digits.length === 8) {
      fetchAddress(digits);
    }
  }, [form.address.zipCode]);

  const validateStep = () => {
    if (step === 0) {
      const docDigits = onlyDigits(form.documentNumber).length;
      const expectedDigits = form.documentType === 'CPF' ? 11 : 14;
      if (!form.legalName || !form.tradeName || docDigits < expectedDigits) {
        return 'Informe razao social, nome fantasia e documento valido.';
      }
      if (!form.companyContactEmail || onlyDigits(form.companyContactPhone).length < 10) {
        return 'Informe email e WhatsApp de contato da empresa.';
      }
      const requiredAddress = ['street', 'number', 'neighborhood', 'city', 'state', 'zipCode'];
      if (requiredAddress.some((field) => !form.address[field])) return 'Preencha o endereco operacional.';
    }
    if (step === 1) {
      if (!form.ownerName || !form.ownerEmail || onlyDigits(form.ownerPhone).length < 10) {
        return 'Informe nome, email e WhatsApp do responsavel.';
      }
    }
    if (step === 2) {
      if (!form.planExternalId) {
        return 'Selecione um plano para continuar.';
      }
      if (!form.termsAccepted || !form.privacyAccepted) {
        return 'Aceite os termos e a politica de privacidade para continuar.';
      }
    }
    return '';
  };

  const buildDraftPayload = (stepIndex) => {
    return {
      draftExternalId: draftId || null,
      step: ['BUSINESS', 'OWNER', 'PLAN'][stepIndex],
      legalName: form.legalName,
      tradeName: form.tradeName,
      documentType: form.documentType,
      documentNumber: onlyDigits(form.documentNumber),
      companyContactEmail: form.companyContactEmail,
      companyContactPhone: onlyDigits(form.companyContactPhone),
      ownerName: form.ownerName,
      ownerEmail: form.ownerEmail,
      ownerPhone: onlyDigits(form.ownerPhone),
      address: {
        ...form.address,
        zipCode: onlyDigits(form.address.zipCode)
      },
      planExternalId: form.planExternalId,
      paymentMethod: 'PIX',
      couponCode: form.couponCode.trim() || null,
      cardBin: null,
      cardLast4: null,
      termsAccepted: form.termsAccepted,
      privacyAccepted: form.privacyAccepted,
      billingAuthorized: false
    };
  };

  const saveDraftStep = async (stepIndex) => {
    setSavingDraft(true);
    try {
      const response = await fetch(`${BACKOFFICE_API_URL}/v1/public/merchant-signup-drafts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildDraftPayload(stepIndex))
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Nao foi possivel salvar esta etapa.');
      setDraftId(data.id);
      localStorage.setItem(DRAFT_STORAGE_KEY, data.id);
      return data;
    } finally {
      setSavingDraft(false);
    }
  };

  const next = async () => {
    const validation = validateStep();
    if (validation) {
      setError(validation);
      return;
    }
    try {
      await saveDraftStep(step);
    } catch (err) {
      setError(err.message || 'Nao foi possivel salvar esta etapa.');
      return;
    }
    setStep((current) => Math.min(current + 1, steps.length - 1));
  };

  const validateCoupon = async () => {
    if (!form.couponCode.trim()) {
      setCouponStatus(null);
      return;
    }
    if (!form.planExternalId) {
      setCouponStatus({ valid: false, reason: 'Selecione um plano antes de validar o cupom.' });
      return;
    }
    try {
      const response = await fetch(`${BACKOFFICE_API_URL}/v1/public/subscription-coupons/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          planExternalId: form.planExternalId,
          code: form.couponCode,
          documentNumber: onlyDigits(form.documentNumber)
        })
      });
      const data = await response.json();
      setCouponStatus(data);
    } catch (err) {
      setCouponStatus({ valid: false, reason: 'Nao foi possivel validar o cupom agora.' });
    }
  };

  const submit = async () => {
    const validation = validateStep();
    if (validation) {
      setError(validation);
      return;
    }
    if (couponStatus && couponStatus.valid === false) {
      setError(couponStatus.reason || 'Cupom invalido.');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      await saveDraftStep(2);
      const response = await fetch(`${BACKOFFICE_API_URL}/v1/public/merchant-signups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          draftExternalId: draftId || null,
          legalName: form.legalName,
          tradeName: form.tradeName,
          documentType: form.documentType,
          documentNumber: onlyDigits(form.documentNumber),
          companyContactEmail: form.companyContactEmail,
          companyContactPhone: onlyDigits(form.companyContactPhone),
          ownerName: form.ownerName,
          ownerEmail: form.ownerEmail,
          ownerPhone: onlyDigits(form.ownerPhone),
          address: {
            ...form.address,
            zipCode: onlyDigits(form.address.zipCode)
          },
          planExternalId: form.planExternalId,
          paymentMethod: 'PIX',
          couponCode: form.couponCode.trim() || null,
          cardBin: null,
          cardLast4: null,
          termsAccepted: form.termsAccepted,
          privacyAccepted: form.privacyAccepted,
          billingAuthorized: false
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Nao foi possivel concluir o cadastro.');
      setSignupStatus(data);
      localStorage.removeItem(DRAFT_STORAGE_KEY);
      setStep(3);
      fetchStatus(data.id);
    } catch (err) {
      setError(err.message || 'Erro ao enviar cadastro.');
    } finally {
      setSubmitting(false);
    }
  };

  const fetchStatus = async (signupId) => {
    if (!signupId) return;
    try {
      const response = await fetch(`${BACKOFFICE_API_URL}/v1/public/merchant-signups/${signupId}/status`);
      if (!response.ok) return;
      const data = await response.json();
      setSignupStatus(data);
    } catch (err) {
      // Status refresh is opportunistic; the submitted signup remains visible.
    }
  };

  const StepIcon = steps[step].icon;

  return (
    <>
      <main className="signup-page">
        <section className="signup-topbar" aria-label="Cadastro Léo Delivery">
          <div className="container">
            <div className="signup-topbar__inner">
              <a className="signup-back-link" href="/#planos">
                <ArrowLeft size={16} /> Voltar para o site
              </a>
            </div>
          </div>
        </section>

        <section className="signup-flow">
          <div className="container">
            <div className="signup-shell">
              <aside className="signup-steps">
                {steps.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <button key={item.id} type="button" className={`signup-step ${index === step ? 'is-active' : ''} ${index < step ? 'is-done' : ''}`} onClick={() => index < step && setStep(index)}>
                      <span><Icon size={18} /></span>
                      {item.label}
                    </button>
                  );
                })}
              </aside>

              <div className="signup-card">
                <div className="signup-card__header">
                  <span><StepIcon size={20} /></span>
                  <div>
                    <h2>{steps[step].label}</h2>
                    <p>{step === 3 ? 'Acompanhe o andamento do seu cadastro.' : 'Preencha os dados para liberar a proxima etapa.'}</p>
                  </div>
                </div>

                {error && (
                  <div className="signup-alert is-error">
                    <AlertCircle size={18} />
                    <span>{error}</span>
                  </div>
                )}

                {step === 0 && (
                  <div className="signup-step-content">
                    <div className="signup-subcard">
                      <div className="signup-subcard__title">
                        <Building2 size={20} />
                        Dados da Empresa
                      </div>
                      <div className="signup-grid">
                        <label>
                          <span className="signup-label-required">Tipo de documento</span>
                          <select value={form.documentType} onChange={(event) => { update('documentType', event.target.value); update('documentNumber', ''); }}>
                            <option value="CNPJ">CNPJ</option>
                            <option value="CPF">CPF</option>
                          </select>
                        </label>
                        <label>
                          <span className="signup-label-required">Documento</span>
                          <input placeholder={form.documentType === 'CPF' ? '000.000.000-00' : '00.000.000/0000-00'} value={form.documentNumber} onChange={(event) => update('documentNumber', event.target.value)} />
                        </label>
                        <label>
                          <span className="signup-label-required">Razao social</span>
                          <input value={form.legalName} onChange={(event) => update('legalName', event.target.value)} />
                        </label>
                        <label>
                          <span className="signup-label-required">Nome fantasia</span>
                          <input value={form.tradeName} onChange={(event) => update('tradeName', event.target.value)} />
                        </label>
                        <label>
                          <span className="signup-label-required">WhatsApp da empresa</span>
                          <input value={form.companyContactPhone} onChange={(event) => update('companyContactPhone', event.target.value)} />
                        </label>
                        <label>
                          <span className="signup-label-required">Email da empresa</span>
                          <input type="email" value={form.companyContactEmail} onChange={(event) => update('companyContactEmail', event.target.value)} />
                        </label>
                      </div>
                    </div>

                    <div className="signup-subcard signup-subcard--gray">
                      <div className="signup-subcard__title">
                        <MapPin size={20} />
                        Endereco Operacional
                      </div>
                      <div className="signup-grid">
                        <div className="signup-grid--cep-street span-2">
                          <label>
                            <span className="signup-label-required">CEP</span>
                            <div className="position-relative">
                              <input placeholder="00000-000" value={form.address.zipCode} onChange={(event) => updateAddress('zipCode', event.target.value)} />
                              {addressLoading && (
                                <div className="position-absolute end-0 top-50 translate-middle-y pe-3">
                                  <Loader2 size={16} className="signup-spin" />
                                </div>
                              )}
                            </div>
                          </label>
                          <label>
                            <span className="signup-label-required">Rua</span>
                            <input value={form.address.street} onChange={(event) => updateAddress('street', event.target.value)} disabled={isAddressFetched || onlyDigits(form.address.zipCode).length !== 8} />
                          </label>
                        </div>
                        <label>
                          <span className="signup-label-required">Numero</span>
                          <input value={form.address.number} onChange={(event) => updateAddress('number', event.target.value)} />
                        </label>
                        <label>
                          Complemento
                          <input value={form.address.complement} onChange={(event) => updateAddress('complement', event.target.value)} />
                        </label>
                        <div className="signup-grid--3 span-2">
                          <label>
                            <span className="signup-label-required">Bairro</span>
                            <input value={form.address.neighborhood} onChange={(event) => updateAddress('neighborhood', event.target.value)} disabled={isAddressFetched || onlyDigits(form.address.zipCode).length !== 8} />
                          </label>
                          <label>
                            <span className="signup-label-required">Cidade</span>
                            <input value={form.address.city} onChange={(event) => updateAddress('city', event.target.value)} disabled={isAddressFetched || onlyDigits(form.address.zipCode).length !== 8} />
                          </label>
                          <label>
                            <span className="signup-label-required">UF</span>
                            <select value={form.address.state} onChange={(event) => updateAddress('state', event.target.value)} disabled={isAddressFetched || onlyDigits(form.address.zipCode).length !== 8}>
                              <option value="">UF</option>
                              {BR_STATES.map((state) => (
                                <option key={state.value} value={state.value}>{state.label}</option>
                              ))}
                            </select>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {step === 1 && (
                  <div className="signup-grid">
                    <label>
                      <span className="signup-label-required">Nome do responsavel</span>
                      <input value={form.ownerName} onChange={(event) => update('ownerName', event.target.value)} />
                    </label>
                    <label>
                      <span className="signup-label-required">Email</span>
                      <input type="email" value={form.ownerEmail} onChange={(event) => update('ownerEmail', event.target.value)} />
                    </label>
                    <label>
                      <span className="signup-label-required">WhatsApp</span>
                      <input value={form.ownerPhone} onChange={(event) => update('ownerPhone', event.target.value)} />
                    </label>
                  </div>
                )}

                {step === 2 && (
                  <div className="signup-step-content">
                    <div className="signup-plan-compare">
                      {plansLoading && (
                        <div className="signup-plan-option is-loading">
                          <Loader2 size={18} className="signup-spin" />
                          Carregando planos...
                        </div>
                      )}
                      {!plansLoading && plans.map((plan) => {
                        const active = form.planExternalId === plan.id;
                        const displayPrice = plan.promotionalPrice || plan.price;
                        return (
                          <button
                            key={plan.id}
                            type="button"
                            className={`signup-plan-option ${active ? 'is-selected' : ''}`}
                            onClick={() => update('planExternalId', plan.id)}
                          >
                            <span className="signup-plan-option__check">{active ? <Check size={16} /> : null}</span>
                            <strong>{plan.name}</strong>
                            <small>{plan.description || 'Plano para operar seu canal proprio com o Leo Delivery.'}</small>
                            <span className="signup-plan-option__price">
                              {formatMoney(displayPrice)}
                              <em>/ {plan.billingPeriod === 'YEARLY' ? 'ano' : 'mes'}</em>
                            </span>
                            {plan.promotionalPrice && <span className="signup-plan-option__promo">{plan.promotionLabel || 'Promocional'}</span>}
                          </button>
                        );
                      })}
                    </div>

                    <div className="signup-grid">
                      <label className="span-2">
                        Cupom
                        <div className="signup-inline">
                          <input value={form.couponCode} onChange={(event) => { update('couponCode', event.target.value.toUpperCase()); setCouponStatus(null); }} />
                          <button type="button" onClick={validateCoupon}>Validar</button>
                        </div>
                      </label>
                      {couponStatus && (
                        <div className={`signup-alert span-2 ${couponStatus.valid ? 'is-success' : 'is-error'}`}>
                          {couponStatus.valid ? <Check size={18} /> : <AlertCircle size={18} />}
                          <span>{couponStatus.valid ? 'Cupom aplicado ao cadastro.' : couponStatus.reason}</span>
                        </div>
                      )}
                      <label className="signup-check span-2">
                        <input type="checkbox" checked={form.termsAccepted} onChange={(event) => update('termsAccepted', event.target.checked)} />
                        <span>
                          Aceito os{' '}
                          <button
                            type="button"
                            className="signup-legal-link"
                            onClick={() => setLegalModal({ title: 'Termos de Uso', document: legalDocuments.terms })}
                          >
                            termos de uso
                          </button>.
                        </span>
                      </label>
                      <label className="signup-check span-2">
                        <input type="checkbox" checked={form.privacyAccepted} onChange={(event) => update('privacyAccepted', event.target.checked)} />
                        <span>
                          Aceito a{' '}
                          <button
                            type="button"
                            className="signup-legal-link"
                            onClick={() => setLegalModal({ title: 'Política de Privacidade', document: legalDocuments.privacy })}
                          >
                            politica de privacidade
                          </button>.
                        </span>
                      </label>
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div className="signup-status">
                    <div className="signup-status__icon">
                      <BadgeCheck size={32} />
                    </div>
                    <h3>Sucesso</h3>
                    <p>{signupStatus?.message || 'Seu cadastro foi recebido e as instrucoes foram enviadas para o email informado.'}</p>
                    {signupStatus?.id && <code>{signupStatus.id}</code>}
                    <div className="signup-status__actions">
                      <a className="btn btn-outline-custom" href="/">
                        Voltar ao site
                      </a>
                      <a className="btn btn-primary-custom" href="https://portal.leodelivery.com.br" target="_blank" rel="noopener noreferrer">
                        Ir para o login
                      </a>
                    </div>
                  </div>
                )}

                <div className="signup-actions">
                  {step > 0 && step < 3 && (
                    <button type="button" className="btn btn-outline-custom" onClick={() => setStep((current) => current - 1)} disabled={savingDraft || submitting}>
                      Voltar
                    </button>
                  )}
                  {step < 2 && (
                    <button type="button" className="btn btn-primary-custom" onClick={next} disabled={savingDraft}>
                      {savingDraft ? <Loader2 size={18} className="signup-spin" /> : null}
                      Continuar
                    </button>
                  )}
                  {step === 2 && (
                    <button type="button" className="btn btn-primary-custom" onClick={submit} disabled={submitting || savingDraft}>
                      {submitting || savingDraft ? <Loader2 size={18} className="signup-spin" /> : <Check size={18} />}
                      Enviar cadastro
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      {legalModal && (
        <div className="signup-legal-modal" role="dialog" aria-modal="true" aria-label={legalModal.title}>
          <div className="signup-legal-modal__dialog">
            <div className="signup-legal-modal__header">
              <h2>{legalModal.title}</h2>
              <button type="button" onClick={() => setLegalModal(null)} aria-label="Fechar">
                <X size={20} />
              </button>
            </div>
            <div className="signup-legal-modal__body">
              <LegalDocument document={legalModal.document} compact />
            </div>
          </div>
        </div>
      )}
      <CookieConsent />
    </>
  );
};

export default SignupPage;
